import { useEffect, useState } from "react";
import { Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, fileUrl } from "@/lib/api";
import { LayoutPicker } from "@/components/LayoutPicker";

const FIELDS = [
  ["name", "Category name"], ["cover_title", "Cover title"], ["intro_title", "Intro page title"],
  ["catalog_title", "Header label (e.g. Sofa Catalogue)"], ["edition", "Edition (e.g. March 2026)"], ["code_prefix", "Product code prefix"],
];
const IMAGES = [["cover_image", "Cover photo"], ["intro_image", "Intro photo"], ["closing_image", "Contact page photo"]];

const ImageField = ({ k, label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    const fd = new FormData(); fd.append("files", file);
    try { const { data } = await api.post("/uploads", fd); onChange(k, data[0].path); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <label className="block cursor-pointer group" data-testid={`category-image-${k}`}>
      <span className="text-[11px] tracking-[0.2em] uppercase">{label}</span>
      <div className="mt-2 aspect-[16/9] overflow-hidden bg-[var(--line)] relative">
        <img src={fileUrl(value, 600)} alt="" className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
        <span className="absolute bottom-2 right-2 bg-[var(--cream)] text-[10px] px-2 py-1 tracking-widest uppercase">{busy ? "Uploading…" : "Replace"}</span>
      </div>
      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
    </label>
  );
};

export const CategoryEditSheet = ({ cat, onSaved }) => {
  const [form, setForm] = useState(cat);
  const [open, setOpen] = useState(false);
  const [brands, setBrands] = useState({ list: [], default: "" });
  const [lays, setLays] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/settings").then((r) => setBrands({ list: r.data.brands.map((b) => b.name), default: r.data.brand_name }));
    api.get("/layouts").then((r) => setLays(r.data.layouts.filter((l) => r.data.enabled.includes(l.id) || l.id === (cat.layout_id || "classic"))));
  }, [open, cat.layout_id]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      await api.put(`/categories/${cat.id}`, form);
      const changed = (form.layout_id || "classic") !== (cat.layout_id || "classic");
      toast.success(changed ? "Layout changed: every product now uses it" : "Catalog pages updated");
      setOpen(false); onSaved(changed || form.style !== cat.style);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(cat); }}>
      <SheetTrigger asChild><Button variant="outline" data-testid="edit-category-button"><Settings2 size={14} className="mr-2" />Edit pages</Button></SheetTrigger>
      <SheetContent className="bg-[var(--cream)] overflow-y-auto w-full sm:max-w-lg">
        <SheetHeader><SheetTitle>Catalog brand, layout &amp; pages</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-6">
          <div><label className="text-[11px] tracking-[0.2em] uppercase">Brand name on this catalog</label>
            <Select value={brands.list.includes(form.brand_name) ? form.brand_name : brands.default} onValueChange={(v) => set("brand_name", v === brands.default ? "" : v)}>
              <SelectTrigger className="mt-1 tracking-widest" data-testid="category-brand-select"><SelectValue /></SelectTrigger>
              <SelectContent>{brands.list.map((b) => <SelectItem key={b} value={b} className="tracking-widest">{b}{b === brands.default ? " (default)" : ""}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-[var(--muted-ink)] mt-1">Switching brand also changes the product codes (SKU prefix), contact page and any brand mentions in the text. Manage brands in Settings.</p></div>
          <div data-testid="category-style-picker"><label className="text-[11px] tracking-[0.2em] uppercase">Catalog style</label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {[["classic", "Classic", "Cream image page + dark configuration table"], ["bespoke", "Bespoke", "White pages, brand logo, half-image data sheet with fabric & leather prices"]].map(([v, n, d]) => (
                <button key={v} type="button" onClick={() => set("style", v)} data-testid={`category-style-${v}`}
                  className={`text-left p-3 border-2 transition-colors ${(form.style || "classic") === v ? "border-[var(--terracotta)]" : "border-[var(--line)]"}`}>
                  <span className="block text-sm font-semibold">{n}</span><span className="block text-[11px] text-[var(--muted-ink)] mt-1">{d}</span>
                </button>
              ))}
            </div></div>
          <div data-testid="category-layout-picker"><label className="text-[11px] tracking-[0.2em] uppercase">Image page layout</label>
            <p className="text-xs text-[var(--muted-ink)] mt-1 mb-2">Every product's image page in this catalog uses this layout. Choose which layouts appear here in Settings.</p>
            <LayoutPicker layouts={lays} value={form.layout_id || "classic"} onChange={(v) => set("layout_id", v)} testid="category-layout" /></div>
          {FIELDS.map(([k, l]) => (
            <div key={k}><label className="text-[11px] tracking-[0.2em] uppercase">{l}</label>
              <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1" data-testid={`category-field-${k}`} /></div>
          ))}
          <div><label className="text-[11px] tracking-[0.2em] uppercase">Intro text — column 1</label>
            <Textarea rows={5} value={form.intro_text_1 || ""} onChange={(e) => set("intro_text_1", e.target.value)} className="mt-1" data-testid="category-field-intro_text_1" /></div>
          <div><label className="text-[11px] tracking-[0.2em] uppercase">Intro text — column 2</label>
            <Textarea rows={4} value={form.intro_text_2 || ""} onChange={(e) => set("intro_text_2", e.target.value)} className="mt-1" data-testid="category-field-intro_text_2" /></div>
          {IMAGES.map(([k, l]) => <ImageField key={k} k={k} label={l} value={form[k]} onChange={set} />)}
          <Button onClick={save} className="w-full" data-testid="save-category-button">Save pages</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
